import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const GestionReferidos = () => {
  const { user, API } = useContext(AuthContext);
  const [referidos, setReferidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos'); // 'todos', 'pendientes', 'otorgados'

  useEffect(() => {
    if (user && user.rol === 'admin') {
      loadReferidos();
    }
  }, [user]);

  const loadReferidos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/referidos`);
      setReferidos(response.data.referidos || []);
    } catch (error) {
      console.error('Error loading referidos:', error);
      toast.error('Error al cargar referidos');
    } finally {
      setLoading(false);
    }
  };

  const handleOtorgarDenarios = async (userId, referidoNombre) => {
    if (!window.confirm(`¿Confirmar que el estudiante ${referidoNombre} completó su pago?\n\nEsto otorgará 100 Denarios al referidor.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/referidos/${userId}/otorgar-denarios`);
      toast.success(response.data.message);
      loadReferidos(); // Recargar lista
    } catch (error) {
      console.error('Error otorgando denarios:', error);
      toast.error(error.response?.data?.detail || 'Error al otorgar denarios');
    }
  };

  const handleGenerarCodigos = async () => {
    if (!window.confirm('¿Generar códigos de referido para todos los usuarios que no lo tienen?\n\nEsto creará códigos únicos para estudiantes y pastores existentes.')) {
      return;
    }

    try {
      toast.info('Generando códigos...');
      const response = await axios.post(`${API}/admin/generar-codigos-referidos-existentes`);
      toast.success(response.data.message);
      loadReferidos(); // Recargar lista
    } catch (error) {
      console.error('Error generando códigos:', error);
      toast.error(error.response?.data?.detail || 'Error al generar códigos');
    }
  };

  const handleEnviarEmails = async () => {
    if (!window.confirm('¿Enviar email a TODOS los estudiantes con su código de referido?\n\nEsto puede tardar unos minutos.')) {
      return;
    }

    try {
      toast.info('Enviando emails... esto puede tardar unos minutos');
      const response = await axios.post(`${API}/admin/enviar-emails-codigos-referidos`);
      toast.success(response.data.message);
      
      if (response.data.errores && response.data.errores.length > 0) {
        toast.warning(`${response.data.errores.length} emails no se pudieron enviar`);
      }
    } catch (error) {
      console.error('Error enviando emails:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar emails');
    }
  };

  const filteredReferidos = referidos.filter(ref => {
    if (filter === 'pendientes') return !ref.denarios_otorgados;
    if (filter === 'otorgados') return ref.denarios_otorgados;
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-UY', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
          <p>Cargando referidos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <i className="fas fa-users text-blue-600"></i>
              Gestión de Referidos
            </h1>
            <p className="text-gray-600 mt-2">
              Administra los estudiantes referidos y otorga Denarios cuando completen su pago
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleGenerarCodigos}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <i className="fas fa-magic mr-2"></i>
              Generar Códigos
            </Button>
            <Button
              onClick={handleEnviarEmails}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <i className="fas fa-envelope mr-2"></i>
              Enviar Emails
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Referidos</p>
                  <p className="text-3xl font-bold text-gray-900">{referidos.length}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <i className="fas fa-users text-blue-600 text-2xl"></i>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pendientes de Pago</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {referidos.filter(r => !r.denarios_otorgados).length}
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <i className="fas fa-clock text-orange-600 text-2xl"></i>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Denarios Otorgados</p>
                  <p className="text-3xl font-bold text-green-600">
                    {referidos.filter(r => r.denarios_otorgados).length}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <i className="fas fa-check-circle text-green-600 text-2xl"></i>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-3">
          <Button
            onClick={() => setFilter('todos')}
            variant={filter === 'todos' ? 'default' : 'outline'}
            className={filter === 'todos' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            Todos ({referidos.length})
          </Button>
          <Button
            onClick={() => setFilter('pendientes')}
            variant={filter === 'pendientes' ? 'default' : 'outline'}
            className={filter === 'pendientes' ? 'bg-orange-600 hover:bg-orange-700' : ''}
          >
            Pendientes ({referidos.filter(r => !r.denarios_otorgados).length})
          </Button>
          <Button
            onClick={() => setFilter('otorgados')}
            variant={filter === 'otorgados' ? 'default' : 'outline'}
            className={filter === 'otorgados' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            Completados ({referidos.filter(r => r.denarios_otorgados).length})
          </Button>
        </div>

        {/* Lista de Referidos */}
        <div className="space-y-4">
          {filteredReferidos.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <i className="fas fa-inbox text-gray-300 text-6xl mb-4"></i>
                <p className="text-gray-500 text-lg">No hay referidos en esta categoría</p>
              </CardContent>
            </Card>
          ) : (
            filteredReferidos.map((ref) => (
              <Card key={ref.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    {/* Info del Referido */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <i className="fas fa-user-graduate text-blue-600 text-xl"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {ref.nombre} {ref.apellido}
                          </h3>
                          <p className="text-sm text-gray-600">{ref.email}</p>
                        </div>
                        <Badge variant={ref.rol === 'estudiante' ? 'default' : 'secondary'}>
                          {ref.rol}
                        </Badge>
                      </div>

                      {/* Info del Referidor */}
                      <div className="ml-15 pl-6 border-l-2 border-gray-200">
                        <p className="text-sm text-gray-600 mb-1">
                          <i className="fas fa-user-friends mr-2 text-gray-400"></i>
                          Referido por:
                        </p>
                        <p className="text-sm font-medium text-gray-800">
                          {ref.referidor?.nombre} {ref.referidor?.apellido}
                        </p>
                        <p className="text-xs text-gray-500">
                          {ref.referidor?.email}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          <i className="fas fa-tag mr-1"></i>
                          Código: {ref.codigo_usado}
                        </p>
                      </div>

                      {/* Fecha de registro */}
                      <p className="text-xs text-gray-500 mt-3">
                        <i className="fas fa-calendar mr-1"></i>
                        Registrado: {formatDate(ref.fecha_registro)}
                      </p>
                    </div>

                    {/* Acciones */}
                    <div className="flex flex-col items-end gap-3">
                      {ref.denarios_otorgados ? (
                        <div className="text-center">
                          <Badge className="bg-green-100 text-green-800 border-green-300">
                            <i className="fas fa-check-circle mr-1"></i>
                            Denarios Otorgados
                          </Badge>
                          <p className="text-xs text-gray-500 mt-2">100 Denarios</p>
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleOtorgarDenarios(ref.id, `${ref.nombre} ${ref.apellido}`)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <i className="fas fa-coins mr-2"></i>
                          Marcar Pagado y Otorgar Denarios
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default GestionReferidos;
